import React from 'react'

import { BorderStyle, Color, Text, withCitrusTheme } from '@gousto-internal/citrus-react'
import type { Recipe } from '@library/menu-service'

const PortionButton = withCitrusTheme('button', (theme) => ({
  alignItems: 'center',
  appearance: 'none',
  backgroundColor: theme.color(Color.ColdGrey_50),
  borderColor: theme.color(Color.ColdGrey_100),
  borderRadius: '999px',
  borderStyle: BorderStyle.Solid,
  borderWidth: theme.border.width(0.5).borderWidth,
  boxSizing: 'border-box',
  color: theme.color(Color.ColdGrey_800),
  cursor: 'pointer',
  display: 'inline-flex',
  font: 'inherit',
  height: 'auto',
  isolation: 'isolate',
  justifyContent: 'center',
  lineHeight: 1,
  maxWidth: '60px',
  minWidth: 0,
  overflow: 'hidden',
  padding: 0,
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',

  '&:hover': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderColor: theme.color(Color.Secondary_400),
  },

  '&:focus-visible': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderColor: theme.color(Color.Secondary_400),
    boxShadow: 'none',
    outline: 'none',
  },

  '&[aria-pressed="true"]': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderColor: theme.color(Color.Secondary_400),
  },

  '&[aria-pressed="true"]:hover, &[aria-pressed="true"]:focus-visible': {
    backgroundColor: theme.color(Color.Secondary_100),
    borderColor: theme.color(Color.Secondary_500),
  },

  '&:disabled:not([aria-pressed="true"])': {
    backgroundColor: theme.color(Color.ColdGrey_50),
    borderColor: theme.color(Color.ColdGrey_100),
    color: theme.color(Color.ColdGrey_300),
    cursor: 'not-allowed',
  },

  '&:disabled:not([aria-pressed="true"])::after': {
    backgroundColor: theme.color(Color.ColdGrey_200),
    content: '""',
    height: theme.border.width(0.5).borderWidth,
    left: 0,
    position: 'absolute',
    top: '50%',
    transform: 'rotate(-45deg)',
    transformOrigin: 'center',
    width: '100%',
    zIndex: 0,
  },

  '& span': {
    position: 'relative',
    zIndex: 1,
  },

  '&:disabled:not([aria-pressed="true"]) span': {
    backgroundColor: theme.color(Color.ColdGrey_50),
    borderRadius: '999px',
    padding: '0 2px',
  },
}))

const PortionPickerGrid = withCitrusTheme('div', (theme) => ({
  display: 'grid',
  gap: `clamp(2px, 0.8vw, ${theme.spacing(1)}px)`,
  gridTemplateColumns: 'repeat(5, minmax(36px, 60px))',
  justifyContent: 'space-between',
  margin: '0 auto',
  maxWidth: '480px',
  width: '100%',
}))

type FlexPortionPickerProps = {
  portions: NonNullable<Recipe['portions']>
  selectedNumPortions: number
  onChange: (numPortions: number) => void
}

export const FlexPortionPicker = ({
  portions,
  selectedNumPortions,
  onChange,
}: FlexPortionPickerProps) => {
  return (
    <PortionPickerGrid>
      {portions.map(({ numPortions, isAvailable }) => {
        const isSelected = numPortions === selectedNumPortions
        const isDisabled = !isAvailable && !isSelected
        const label = `${numPortions.toString()} ${numPortions === 1 ? 'portion' : 'portions'}${
          isAvailable ? '' : ' unavailable'
        }`

        return (
          <PortionButton
            aria-label={label}
            aria-pressed={isSelected}
            disabled={isDisabled}
            key={numPortions}
            onClick={() => onChange(numPortions)}
            type="button"
          >
            <Text size={2}>{numPortions.toString()}</Text>
          </PortionButton>
        )
      })}
    </PortionPickerGrid>
  )
}
